/**
 * Script Editor Section
 *
 * Custom behavior editor for ScriptBehavior instances.
 * Shows an inline TypeScript code editor with Save/Revert and compilation error display.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ScriptEditor } from '../components/script-editor';
import type { ScriptBehavior } from 'trace-viewer/behaviors';

export interface ScriptEditorSectionProps {
    behavior: ScriptBehavior;
}

export const ScriptEditorSection: React.FC<ScriptEditorSectionProps> = ({
    behavior,
}) => {
    const [sourceText, setSourceText] = useState('');
    const [savedSource, setSavedSource] = useState('');
    const [compileError, setCompileError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const behaviorType = behavior.getData()['@type'];
    const traceScene = behavior.sceneObject.traceScene;
    const resources = behavior.sceneObject.resources;

    // Load script source from ResourceManager on mount
    useEffect(() => {
        const loadSource = async () => {
            const scriptsMap = traceScene.scriptsMap;
            const assetPath = scriptsMap?.[behaviorType];
            if (!assetPath) {
                setSourceText(`// No script source found for @type "${behaviorType}"`);
                setSavedSource('');
                setLoading(false);
                return;
            }

            const normalizedPath = assetPath.replace(/^\.\//, '');
            const blob = resources.getAsset(normalizedPath) ?? resources.getAsset(assetPath);
            if (!blob) {
                setSourceText(`// Script asset not found: ${assetPath}`);
                setSavedSource('');
                setLoading(false);
                return;
            }

            const text = await blob.text();
            setSourceText(text);
            setSavedSource(text);
            setLoading(false);

            // Check for existing compilation errors
            const error = traceScene.scriptRegistry.getError(behaviorType);
            if (error) setCompileError(error);
        };

        void loadSource();
    }, [behaviorType, traceScene, resources]);

    const isDirty = sourceText !== savedSource;

    const handleSave = useCallback(() => {
        const scriptsMap = traceScene.scriptsMap;
        const assetPath = scriptsMap?.[behaviorType];
        if (!assetPath) return;

        // Update blob in ResourceManager
        const normalizedPath = assetPath.replace(/^\.\//, '');
        resources.addAsset(normalizedPath, new Blob([sourceText], { type: 'text/typescript' }));

        // Re-register in ScriptRegistry
        traceScene.scriptRegistry.register(behaviorType, sourceText);

        // Check for compilation errors
        const error = traceScene.scriptRegistry.getError(behaviorType);
        if (error) {
            setCompileError(error);
            return;
        }

        setCompileError(null);
        setSavedSource(sourceText);

        // Hot-reload the behavior so new code takes effect
        const factory = traceScene.scriptRegistry.getFactory(behaviorType);
        if (factory) {
            behavior.reloadFactory(factory);
        }
    }, [sourceText, behaviorType, traceScene, resources, behavior]);

    const handleRevert = useCallback(() => {
        setSourceText(savedSource);
        setCompileError(traceScene.scriptRegistry.getError(behaviorType) ?? null);
    }, [savedSource, traceScene, behaviorType]);

    if (loading) {
        return <div style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>Loading script...</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minHeight: 0 }}>
            {/* Script source editor */}
            <ScriptEditor
                value={sourceText}
                onChange={setSourceText}
                error={compileError}
            />

            {/* Save / Revert buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
                <button
                    onClick={handleSave}
                    disabled={!isDirty}
                    style={{
                        flex: 1,
                        padding: '6px 12px',
                        backgroundColor: isDirty ? '#0078d4' : '#444',
                        border: 'none',
                        borderRadius: '3px',
                        color: '#fff',
                        cursor: isDirty ? 'pointer' : 'not-allowed',
                        fontSize: '12px',
                    }}
                >
                    Save Script
                </button>
                <button
                    onClick={handleRevert}
                    disabled={!isDirty}
                    style={{
                        padding: '6px 12px',
                        backgroundColor: isDirty ? '#555' : '#444',
                        border: 'none',
                        borderRadius: '3px',
                        color: '#fff',
                        cursor: isDirty ? 'pointer' : 'not-allowed',
                        fontSize: '12px',
                    }}
                >
                    Revert
                </button>
            </div>
        </div>
    );
};
